import type { ExchangeSummary } from '@/api/capture';
import { useLowData } from '@/capture/lowData';
import { track } from '@/flow/instrument';
import { chooseLocale } from '@/i18n/persistLocale';
import { NATIVE_NAMES, type PackLocales, chooserLocales, useT } from '@/i18n/runtime';
import type { TenantConfig } from '@/theme/tenant';
import { AppBar } from './components/AppBar';
import { Icon } from './components/Icon';
import { HelpStepMarker } from './help/HelpContext';

/**
 * The language chooser (SPEC_CAPTURE_UX_R2 §2.1) — the first screen after the front door, before consent: one
 * column of big rows, each language in its OWN script (a speaker finds their own), the tenant's declared
 * preference first and marked "Suggested", nothing pre-selected. Only content-backed languages are offered
 * (`chooserLocales()`: a complete deck — today en + hi; the other nine render nothing until their decks land, P10).
 * A tap sets the runtime (every visible string re-renders), stores it on this device and persists it on the case
 * (`PATCH /locale`, best-effort). The low-data toggle (§7.4) lives here too: on by itself on a slow connection.
 */
/**
 * The chooser's rows — ONE implementation for the chooser screen and (VC-12 r5) the OTP code screen: each language in
 * its OWN script, the tenant's preference first and marked "Suggested", nothing pre-selected unless `current` is given
 * (the code screen marks the language on screen with `aria-pressed`). A tap sets the runtime, stores it on this device
 * and persists it on the case when a bearer exists (`chooseLocale`).
 */
export function LanguageRows({
  packLocales,
  preferred = null,
  current = null,
  onPick,
}: {
  packLocales: PackLocales;
  preferred?: string | null;
  current?: string | null;
  onPick: (code: string) => void;
}): JSX.Element {
  const { t, setLocale } = useT();
  // VC-12: Arabic joins the rows only when the case's pack lists it (`packLocales`; absent on India cases)
  const offered = chooserLocales(packLocales);
  const suggested = preferred && offered.includes(preferred) ? preferred : null;
  const rows = suggested ? [suggested, ...offered.filter((c) => c !== suggested)] : offered;
  const pick = (code: string): void => {
    // §10 "Language chooser → language distribution by tenant/region": the code and whether it was the suggested row.
    track('lang.choose', { code, suggested: code === suggested });
    void chooseLocale(code, setLocale);
    onPick(code);
  };
  return (
    <ul className="sv-lang-list" data-testid="language-list">
      {rows.map((code) => (
        <li key={code}>
          <button
            type="button"
            lang={code}
            className="sv-lang-row"
            onClick={() => pick(code)}
            data-testid={`language-${code}`}
            data-suggested={code === suggested ? 'true' : 'false'}
            aria-pressed={current === null ? undefined : code === current}
          >
            <Icon name="chevron" size={16} className="ic" />
            <span>{NATIVE_NAMES[code] ?? code}</span>
            {code === suggested ? <span className="sv-lang-sug">{t('sv.lang.suggested')}</span> : null}
          </button>
        </li>
      ))}
    </ul>
  );
}

export function LanguageChooser({ tenant, summary, onChosen }: { tenant: TenantConfig; summary: ExchangeSummary; onChosen: (code: string) => void }): JSX.Element {
  const { t } = useT();
  const lowData = useLowData();
  return (
    <div className="app-view" data-testid="language-chooser">
      <HelpStepMarker screen="language" tenantName={tenant.name} />
      <AppBar tenant={tenant} subtitle={t('sv.subtitle')} />
      <div className="stack">
        <div>
          <div className="h-title sm">
            <Icon name="globe" size={18} /> {t('sv.lang.title')}
          </div>
          <p className="sub-sm" style={{ marginTop: 6 }}>
            {t('sv.lang.sub')}
          </p>
        </div>
        <LanguageRows packLocales={summary.packLocales} preferred={summary.locale} onPick={onChosen} />
        <button type="button" className="sv-switch-row" aria-pressed={lowData.on} onClick={() => lowData.setManual(!lowData.on)} data-testid="low-data-toggle" data-source={lowData.source}>
          <Icon name="cloud" size={18} />
          <div>
            <div className="pt">{t('sv.lang.lowData.title')}</div>
            <div className="pd">{lowData.source === 'network' ? t('sv.lang.lowData.auto') : t('sv.lang.lowData.sub')}</div>
          </div>
          <span className={`sv-switch${lowData.on ? ' is-on' : ''}`} aria-hidden="true">
            <i />
          </span>
        </button>
      </div>
    </div>
  );
}
